import { z } from "zod";
import { IsoDateTime, jsonColumn } from "./common.ts";
import { MbEnrichRunSchema } from "./mbEnrich.ts";
import { SyncRunSchema } from "./syncRun.ts";
import {
  MirrorInfoSchema,
  SpotifyApiDayStatsSchema,
} from "./spotifyApiLog.ts";

/** What Spotify returns from GET /v1/me — the bits we keep. */
export const SpotifyUserSchema = z.object({
  id: z.string().min(1),
  display_name: z.string().nullable(),
  email: z.string().nullable(),
  product: z.string().nullable(),
  image_url: z.string().nullable(),
  country: z.string().nullable().optional(),
  fetched_at: IsoDateTime,
});

export type SpotifyUser = z.infer<typeof SpotifyUserSchema>;

/**
 * Tokens live in `meta`, never in `.env` and never in a tokens.json next to the
 * source. Refresh is durable; access is cached in memory until expires_at.
 */
export const SpotifyTokensSchema = z.object({
  access_token: z.string().min(1),
  refresh_token: z.string().min(1),
  /** Absolute expiry of the access token. */
  expires_at: IsoDateTime,
  scope: z.string().nullable(),
  token_type: z.string().default("Bearer"),
  updated_at: IsoDateTime,
});

export type SpotifyTokens = z.infer<typeof SpotifyTokensSchema>;

export const QuotaStatusSchema = z.object({
  active: z.boolean(),
  ready_at: IsoDateTime.nullable(),
  started_at: IsoDateTime.nullable(),
  remaining_sec: z.number().int().nonnegative(),
  total_sec: z.number().int().nonnegative(),
  reason: z.enum(["RATE_LIMIT", "QUOTA_EXCEEDED"]).nullable(),
});

export type QuotaStatus = z.infer<typeof QuotaStatusSchema>;

export const LibraryCountsSchema = z.object({
  tracks: z.number().int().nonnegative(),
  albums: z.number().int().nonnegative(),
  artists: z.number().int().nonnegative(),
  playlists: z.number().int().nonnegative(),
  liked: z.number().int().nonnegative(),
  /** Rows that already have a Spotify CDN cover URL in the mirror. */
  tracks_with_cover: z.number().int().nonnegative(),
  albums_with_cover: z.number().int().nonnegative(),
  /** `map_version = 0` — still waiting for a live sync remap (covers, popularity…). */
  tracks_unmapped: z.number().int().nonnegative(),
});

export type LibraryCounts = z.infer<typeof LibraryCountsSchema>;

/**
 * Per-user mirror completeness — numbers you can compare to Spotify
 * (Liked Songs count, playlist count, owned vs followed, etc.).
 */
export const LibraryCompletenessSchema = z.object({
  liked: z.number().int().nonnegative(),
  playlists_listed: z.number().int().nonnegative(),
  playlists_owned: z.number().int().nonnegative(),
  playlists_followed: z.number().int().nonnegative(),
  /** Owned/collaborative playlists with at least one mirrored track row. */
  playlists_with_tracks: z.number().int().nonnegative(),
  playlists_empty: z.number().int().nonnegative(),
  /** Listed but items not fetchable (usually followed-only under Dev Mode). */
  playlists_items_inaccessible: z.number().int().nonnegative(),
  /** Sum of playlist_tracks rows across this user’s listed playlists. */
  playlist_track_rows: z.number().int().nonnegative(),
  /** Distinct tracks in liked ∪ this user’s Spotify playlists. */
  unique_tracks: z.number().int().nonnegative(),
  unique_albums: z.number().int().nonnegative(),
  unique_artists: z.number().int().nonnegative(),
});

export type LibraryCompleteness = z.infer<typeof LibraryCompletenessSchema>;

/** Compact last-sync summary for the status endpoint. */
export const SyncSummarySchema = z.object({
  id: z.number().int(),
  status: SyncRunSchema.shape.status,
  phase: SyncRunSchema.shape.phase,
  started_at: IsoDateTime,
  finished_at: IsoDateTime.nullable(),
  api_calls: z.number().int().nonnegative(),
  error: z.string().nullable(),
});

export type SyncSummary = z.infer<typeof SyncSummarySchema>;

/**
 * Last real Spotify HTTP 429 we observed (not our soft “hot window” tip).
 * Survives so Settings can show what tripped the API.
 */
export const SpotifyLast429Schema = z.object({
  at: IsoDateTime,
  path: z.string().min(1),
  label: z.string().nullable(),
  retry_after_sec: z.number().nonnegative(),
  reason: z.enum(["RATE_LIMIT", "QUOTA_EXCEEDED"]),
  body_snippet: z.string().nullable(),
  /** true = we slept and retried; false = run stopped / cooldown set. */
  recovered: z.boolean(),
});

export type SpotifyLast429 = z.infer<typeof SpotifyLast429Schema>;

/**
 * Connection + library snapshot. `configured` means app credentials are in env;
 * `connected` means a refresh token is in the DB.
 */
export const AppStatusSchema = z.object({
  configured: z.boolean(),
  connected: z.boolean(),
  api_ok: z.boolean(),
  user: SpotifyUserSchema.nullable(),
  quota: QuotaStatusSchema,
  sync: SyncSummarySchema.nullable(),
  counts: LibraryCountsSchema,
  /** This signed-in user’s library vs Spotify — not the whole shared catalog. */
  completeness: LibraryCompletenessSchema,
  /** Today's Spotify HTTP counters from the persistent call log. */
  api_today: SpotifyApiDayStatsSchema,
  /** Whether the SQLite mirror came from offline import or a live sync. */
  mirror: MirrorInfoSchema,
  /** Last real Spotify 429 (from meta), if any. */
  last_429: SpotifyLast429Schema.nullable(),
  error: z.string().nullable(),
  /** Spotify Dev Mode allowlist miss — actionable help for the toast. */
  allowlist: z.boolean().optional(),
  help: z.string().nullable().optional(),
});

export type AppStatus = z.infer<typeof AppStatusSchema>;

/** Pending OAuth CSRF state, stored briefly while the user is at Spotify. */
export const OauthStateSchema = z.object({
  state: z.string().min(16),
  /** CoyKoi user who started Connect — callback writes tokens to this row. */
  user_id: z
    .string()
    .regex(/^usr_[A-Za-z0-9_-]{8,32}$/, "Expected a CoyKoi user id"),
  created_at: IsoDateTime,
  /** Absolute expiry; past this the callback rejects. */
  expires_at: IsoDateTime,
  /** Browser origin to return to after Spotify (Vite 5173 or the API itself). */
  return_to: z.string().url().nullable().optional(),
});

export type OauthState = z.infer<typeof OauthStateSchema>;

/** Known meta keys and the schemas their values must satisfy. */
export const META_KEYS = {
  last_sync_at: IsoDateTime,
  spotify_last_429: SpotifyLast429Schema,
  ui_prefs: jsonColumn(z.record(z.string(), z.unknown())),
  /** MusicBrainz User-Agent contact override (falls back to MUSICBRAINZ_CONTACT). */
  mb_contact: z.string().min(3).max(200),
  /** Live / last MB enrichment run. Durable so a restart can resume. */
  mb_enrich_run: MbEnrichRunSchema,
  /** Research prompt template with {{ANCHORS}} / {{CATEGORIES}} placeholders. */
  research_prompt_template: z.string().min(1).max(50_000),
  /** Follow-up research clipboard template (same placeholders). */
  research_continue_prompt_template: z.string().min(1).max(50_000),
} as const;

export type MetaKey = keyof typeof META_KEYS;
